/**
 * <role-audit-log> — reverse-chronological audit-log table.
 *
 * Generalized for both account-tier and property-tier. Pass URL via
 * `audit-url`, or `account-id` for back-compat account-tier default.
 *
 *   <role-audit-log account-id="..."></role-audit-log>
 *   <role-audit-log audit-url="/api/.../properties/.../audit-log"></role-audit-log>
 */

const ACTION_LABELS = {
  'role.created': 'Role created',
  'role.cloned': 'Role cloned',
  'role.renamed': 'Role renamed',
  'role.deleted': 'Role deleted',
  'role.permission.added': 'Permissions granted',
  'role.permission.removed': 'Permissions revoked',
  'user.role_changed': 'User role changed',
  'user.role_assigned': 'User assigned',
  'user.role_unassigned': 'User unassigned',
};

class RoleAuditLog extends HTMLElement {
  static get observedAttributes() {
    return ['account-id', 'audit-url'];
  }

  constructor() {
    super();
    this._initialized = false;
    this._entries = [];
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this._scopedClick = (e) => {
      if (this.contains(e.target)) this._onClick(e);
    });
    if (!this._initialized) {
      this._initialized = true;
      this._renderLoading();
    }
    if (this._auditUrl()) {
      this.refresh();
    }
  }

  disconnectedCallback() {
    if (this._scopedClick) document.removeEventListener('click', this._scopedClick);
    this._scopedClick = null;
  }

  _auditUrl() {
    const explicit = this.getAttribute('audit-url');
    if (explicit) return explicit;
    const accountId = this.getAttribute('account-id');
    if (accountId) return `/api/accounts/${accountId}/audit-log`;
    return null;
  }

  async refresh() {
    try {
      const url = this._auditUrl();
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(
        `${url}${sep}limit=100`,
        { headers: { Accept: 'application/json' }, credentials: 'include' }
      );
      if (!res.ok) {
        this._renderError('Failed to load audit log');
        return;
      }
      this._entries = await res.json();
      this._render();
    } catch (err) {
      this._renderError(String(err));
    }
  }

  _onClick(e) {
    if (e.target.closest('[data-audit-log-action="refresh"]')) {
      e.preventDefault();
      this.refresh();
    }
  }

  _escape(s) {
    return String(s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  _formatDate(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  _formatDetail(entry) {
    const d = entry.detail || {};
    const parts = [];
    if (d.name) parts.push(`Name: ${d.name}`);
    if (d.old_name && d.new_name) {
      parts.push(`${d.old_name} → ${d.new_name}`);
    }
    if (d.source_name) parts.push(`Cloned from: ${d.source_name}`);
    if (Array.isArray(d.added) && d.added.length) {
      parts.push(`+ ${d.added.join(', ')}`);
    }
    if (Array.isArray(d.removed) && d.removed.length) {
      parts.push(`− ${d.removed.join(', ')}`);
    }
    return parts.join(' · ');
  }

  _renderLoading() {
    this.innerHTML = `
      <div class="role-audit-log-loading luboo-empty-state">
        <p>Loading audit log…</p>
      </div>
    `;
  }

  _renderError(msg) {
    this.innerHTML = `
      <div class="role-audit-log-error luboo-empty-state">
        <p>Couldn't load audit log: ${this._escape(msg)}</p>
        <button class="btn btn-secondary" data-audit-log-action="refresh">
          Retry
        </button>
      </div>
    `;
  }

  _render() {
    if (!this._entries.length) {
      this.innerHTML = `
        <div class="role-audit-log-empty luboo-empty-state">
          <p>No role or permission changes yet.</p>
        </div>
      `;
      return;
    }
    const rows = this._entries
      .map(
        (e) => `
          <tr>
            <td class="audit-when">${this._escape(this._formatDate(e.created_at))}</td>
            <td class="audit-action">${this._escape(ACTION_LABELS[e.action] || e.action)}</td>
            <td class="audit-detail">${this._escape(this._formatDetail(e))}</td>
          </tr>
        `
      )
      .join('');
    this.innerHTML = `
      <div class="role-audit-log">
        <table class="role-audit-log-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }
}

export { RoleAuditLog };
